<script lang="ts">
    import type {Category, SearchQuery, TermValuesResponse} from "../NvbsSearchEngineApi/interfaces";
    import {NvbsSearchEngineApi} from "../NvbsSearchEngineApi/NvbsSearchEngineApi";
    import {onMount} from "svelte";
    import Svg from "../common/Svg.svelte";
    import TermValueSelect from "../TermValueSelect.svelte";
    import {fly} from "svelte/transition";
    import DateSelect from "./DateSelect.svelte";
    import type {UrlState} from "../common/SearchEngineUrlState";

    const categoriesWithoutDate: string[] = [
        'wordpress_all', // Alle
        'wordpress_division', // Afdelingen
        'wordpress_product' // Webshop
    ];

    let {
        searchStore = $bindable(),
        selectedCategory,
        hasBackButton = false,
        query = '',
        onback = () => {
            // Default back button action
        },
        setSearchTermValues = (values: { [term: string]: string[] }) => {
        },
    }: {
        searchStore: SearchQuery,
        selectedCategory: Category;
        hasBackButton?: boolean;
        onback?: () => void;
        query?: string;
        setSearchTermValues?: (values: { [term: string]: string[] }) => void;
        initialState: UrlState
    } = $props();

    let terms = $state<{
        [name: string]: string
    }>({});


    let isDateCategory = $derived.by(() => {
        return !categoriesWithoutDate.includes(selectedCategory.name);
    });

    let isReady = $state(false);

    let selectedTerm = $state<string | null>(null);

    let date_select = $state(false);

    let selectedTermValues: {
        [term: string]: string[]
    } = $state({});

    onMount(function () {
        selectedTermValues = {...searchStore.terms};
        isReady = true;
    })

    $effect(() => {
        if(!isReady) return;
        setSearchTermValues(selectedTermValues);
    })

    let termValues = $state<TermValuesResponse | null>(null);

    $effect(() => {
        if (selectedTerm) {
            NvbsSearchEngineApi.getTermValues({
                category: selectedCategory.name,
                term: selectedTerm,
                existing_terms: selectedTermValues,
                label: selectedCategory.label,
                query: query,
                enable_ocr: searchStore.ocr,
                date_one: searchStore.date_one,
                date_two: searchStore.date_two,
                exact_match: searchStore.exact_match
            }).then((response => {
                termValues = response;
            }));
        } else {
            termValues = null;
        }
    })

    onMount(() => {
        NvbsSearchEngineApi.getTerms({
            category: selectedCategory.name,
        }).then((response => {
            terms = response;
        }));
    })

    function clickDateSelect() {
        date_select = !date_select;
        if (date_select) {
            selectedTerm = null;
        }
    }

    function timestampToDate(timestamp: string | number): string {
        const date = new Date(timestamp);
        return date.toLocaleDateString('nl-NL', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit'
        }).replace(/-/g, '/');
    }
</script>

<h3>{selectedCategory.label}</h3>


<div class="search-terms">
    {#if hasBackButton}
        <button class="back-button btn-primary"
                onclick={onback}
        >
            <Svg type="chevron-left"/>
            Terug
        </button>
    {/if}
    {#each Object.entries(terms) as [name, label]}
        <button class="search-term btn-primary--light"
                onclick={() => {
                    if (selectedTerm === name) {
                        selectedTerm = null;
                    } else {
                        selectedTerm = name;
                    }
                    if (date_select) {
                        date_select = false;
                    }
            }}
        >
            {label}
            {#if selectedTerm === name}
                <Svg type="minus"/>
            {:else}
                <Svg type="plus"/>
            {/if}
        </button>
    {/each}
    {#if selectedCategory.name === 'nvbsodr'}
        <button class="ocr-button btn-primary--light"
                class:active={searchStore.ocr}
                onclick={() => {
                    searchStore.ocr = !searchStore.ocr;
                }}
        >
            {#if searchStore.ocr}
                <Svg type="check"/>
            {:else}
                <Svg type="cross"/>
            {/if}
            tekst doorzoeken
        </button>
    {/if}
    <button
        class="ocr-button btn-primary--light"
        class:active={searchStore.exact_match}
        onclick={() => {
            searchStore.exact_match = !searchStore.exact_match;
        }}
    >
        {#if searchStore.exact_match}
            <Svg type="check"/>
        {:else}
            <Svg type="cross"/>
        {/if}
        Exact zoeken

    </button>
    {#if isDateCategory}
        <button class="search-term btn-primary--light"
                onclick={clickDateSelect}
        >
            Periode
            {#if date_select}
                <Svg type="minus"/>
            {:else}
                <Svg type="plus"/>
            {/if}
        </button>
    {/if}
</div>
<div class="current-terms">
    {#each Object.entries(terms) as [name, label]}
        {#each selectedTermValues[name] as value (value)}
            <div class="current-term btn-primary--light"
                 transition:fly|global={{duration: 200, x: -50}}
            >
                {label}: {value}
                <button class="remove-button"
                        onclick={() => {
                    selectedTermValues[name] = selectedTermValues[name].filter((v) => v !== value);
                }}
                >
                    <Svg type="times"/>
                </button>
            </div>
        {/each}
    {/each}

    <!--    TODO: Make this better-->
    {#if (searchStore.date_one || searchStore.date_two)}
        <div class="current-term btn-primary--light"
             transition:fly|global={{duration: 200, x: -50}}
        >
            {#if searchStore.date_one === searchStore.date_two}
                Periode: {timestampToDate(searchStore.date_one)}
            {:else}
                Periode: {timestampToDate(searchStore.date_one)} - {timestampToDate(searchStore.date_two)}
            {/if}
            <button class="remove-button"
                    onclick={() => {
                searchStore.date_one = undefined;
                searchStore.date_two = undefined;
                date_select = false;
            }}
            >
                <Svg type="times"/>
            </button>
        </div>
    {/if}
</div>
{#key selectedTerm}
    {#if termValues && selectedTerm}
        <TermValueSelect
                termValueResponse={termValues}
                confirm={(values, term) => {
            selectedTermValues = {...selectedTermValues, [term]: values};
            selectedTerm = null;
        }}
                cancel={() => {
            selectedTerm = null;
        }}
                selection={
            selectedTermValues[selectedTerm] || []
        }
        />
    {/if}
{/key}
{#if date_select}
    <DateSelect
            bind:searchStore
            startYear={searchStore.date_one}
            endYear={searchStore.date_two}
            cancel={() => {
        date_select = false;
        selectedTerm = null;
    }}/>
{/if}

<style>

    .search-terms {
        display: flex;
        justify-content: left;
        align-items: center;
        gap: 10px;
        margin-bottom: 24px;
        flex-wrap: wrap;
    }

    .search-term, .back-button, .ocr-button, .current-term {
        font-size: 14px;
        padding: 0.25rem 0.5rem;
        min-height: 2rem;
    }

    .ocr-button.active {
        background: #1F4588;
        border-color: #1F4588;
        color: white;
    }

    .current-terms {
        display: flex;
        flex-wrap: wrap;
        gap: 10px;
        align-items: center;

        margin-bottom: 24px;

        .current-term {
            display: flex;
            gap: 5px;
            align-items: center;
            background: #f0f0f0;
            padding: 5px;
            border-radius: 5px;

            button {
                border: none;
                background: none;
                cursor: pointer;
                color: #375698;
            }
        }
    }
</style>
